'use client';

import { useMemo } from 'react';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';
import { PortableTextBlock } from 'sanity';
import AutoScroll from 'embla-carousel-auto-scroll';
import { GROQImage } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { Carousel, CarouselContent, CarouselItem } from '@/shadcn/ui/carousel';
import { FadeIn } from '@/components/global/utils/FadeIn';
import { PipeChip } from './_shared';

export interface ICourse {
  image?: GROQImage;
  name?: string;
}

interface CoursesSectionProps {
  chip?: string;
  title?: PortableTextBlock[];
  courses?: ICourse[];
}

// One course: a portrait thumbnail with a giant, faded index numeral tucked in
// behind its left edge (Figma 1845:2279 — number `mr` pulls the card over it).
function CourseSlide({ course, index }: { course: ICourse; index: number }) {
  return (
    <CarouselItem className="!pl-[1.125rem] basis-auto">
      <div className="flex items-end h-[40rem] lg:h-[60.5rem]">
        <span
          aria-hidden
          className="text-center pointer-events-none leading-[0.75] select-none shrink-0 font-sans font-bold text-white/10 text-[56rem] lg:text-[84.3rem] tracking-[-2.3rem] lg:tracking-[-3.5rem] w-[26rem] lg:w-[39.27rem] mr-[-7.5rem] lg:mr-[-11.25rem]"
        >
          {index + 1}
        </span>
        <div className="overflow-hidden relative shrink-0 rounded-[1.5rem] bg-white/[0.04] h-[40rem] w-[26.6rem] lg:h-[60.5rem] lg:w-[40.44rem]">
          {course.image && (
            <Image
              src={course.image}
              alt={course.name ?? ''}
              fill
              quality={100}
              sizes="(max-width: 992px) 26.6rem, 40.44rem"
              className="object-cover"
            />
          )}
        </div>
      </div>
    </CarouselItem>
  );
}

export default function CoursesSection({
  chip,
  title,
  courses,
}: CoursesSectionProps) {
  const autoScroll = useMemo(
    () => AutoScroll({ speed: 1, startDelay: 0, stopOnInteraction: false }),
    [],
  );

  if (!courses || courses.length === 0) return null;

  // Duplicate the set so the loop stays gap-free even with only a handful of
  // courses; the numeral still counts against the original order.
  const slides = [...courses, ...courses];

  return (
    <section className="overflow-hidden relative w-full bg-black py-[15rem]">
      <div className="flex flex-col items-center mx-auto text-center w-[min(150rem,100%)] px-[2rem] lg:px-0 gap-[3rem]">
        {chip && (
          <FadeIn delay={0}>
            <PipeChip>{chip}</PipeChip>
          </FadeIn>
        )}
        {title && (
          <FadeIn
            delay={0.1}
            className="richtext prose-em:font-news prose-em:italic prose-headings:font-news prose-headings:leading-[1.1] prose-headings:tracking-[-0.14rem] lg:prose-headings:tracking-[-0.25rem] prose-headings:text-white/90 prose-headings:m-0 prose-headings:text-[3.4rem] lg:prose-headings:text-[6rem] prose-p:font-news prose-p:leading-[1.1] prose-p:tracking-[-0.14rem] lg:prose-p:tracking-[-0.25rem] prose-p:text-white/90 prose-p:m-0 prose-p:text-[3.4rem] lg:prose-p:text-[6rem] max-w-[66.25rem] [text-wrap:balance]">
            <PortableText value={title} components={richTextComponent} />
          </FadeIn>
        )}
      </div>

      {/* Full-bleed auto-scrolling course marquee */}
      <Carousel
        opts={{ loop: true, align: 'start' }}
        plugins={[autoScroll]}
        className="mt-[8rem] w-full"
      >
        <CarouselContent className="ml-0">
          {slides.map((course, i) => (
            <CourseSlide key={i} course={course} index={i % courses.length} />
          ))}
        </CarouselContent>
      </Carousel>
    </section>
  );
}
